import React, { useEffect, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useInView } from "framer-motion";

const PaymentChart = ({ data, currency }) => {
  const chartRef = useRef(null);
  const isInView = useInView(chartRef, { once: true, margin: "-100px" });
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    if (isInView) {
      setTimeout(() => {
        setShowChart(true);
      }, 500);
    }
  }, [isInView]);

  // Process time slots (assuming data comes in format like ["09:00", "10:00", ...])
  const timeSlots = data.map((item) => item.paymentDesc);
  const salesData = data.map((item) => item.amount);

  // Gradient colors for the area chart
  const gradientColors = {
    gradientTo: "rgba(99, 102, 241, 0.1)", // indigo-500 with opacity
    gradientFrom: "rgba(79, 70, 229, 0.3)", // indigo-600 with opacity
    strokeColor: "rgba(79, 70, 229, 1)", // indigo-600 solid
  };

  const chartOptions = {
    chart: {
      type: "area",
      height: 300,
      background: "transparent",
      foreColor: "#FFFFFF",
      toolbar: { show: false },
    },

    colors: ["#FFFFFF"], // ✅ LINE COLOR WHITE

    stroke: {
      curve: "smooth",
      width: 2,
      colors: ["#FFFFFF"], // ✅ ensure line is white
    },

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4, // 🔥 reduce this
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },

    dataLabels: {
      enabled: true,
      formatter: (val) => {
        const num = Number(val ?? 0);
        if (!num) return "";

        if (num >= 1_000_000) {
          return ` ${(num / 1_000_000).toFixed(1)}M`;
        }
        if (num >= 1_000) {
          return ` ${(num / 1_000).toFixed(1)}K`;
        }

        return ` ${num.toFixed(1)}`;
      },
      style: {
        fontSize: "11px",
        fontFamily: "QSemiBold, sans-serif",
        fontWeight: 600,
        colors: ["#FFFFFF"],
      },

      // 👇 Dynamic offset logic
      offsetY: 0,

      distributed: true,

      // 👇 Use custom logic via formatter + CSS trick
      background: {
        enabled: true,
        foreColor: "#000",
        borderRadius: 4,
        padding: 2,
        opacity: 0.7,
      },
    },

    tickPlacement: "on",
    xaxis: {
      categories: timeSlots,
      tickPlacement: "on",
      range: timeSlots.length - 1,
      labels: {
        trim: false,
        style: {
          fontSize: "12px",
          fontFamily: "QSemiBold, sans-serif",
          colors: timeSlots.map(() => "#FFFFFF"), // ✅ force all labels white
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },

    yaxis: {
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
          if (val >= 1000) return (val / 1000).toFixed(1) + "K";
          return val.toFixed(0);
        },
        style: {
          fontSize: "12px",
          fontFamily: "QSemiBold, sans-serif",
          colors: ["#FFFFFF"],
        },
      },
    },

    grid: {
      padding: {
        left: 20,
        right: 20,
      },
      strokeDashArray: 4,
      borderColor: "rgba(255,255,255,0.1)",
    },

    tooltip: {
      theme: "dark",
      y: {
        formatter: (val) => `${currency} ${val.toFixed(2)}`,
      },
    },

    markers: {
      size: 5,
      offsetX: 0,
      strokeWidth: 2,
      colors: ["#FFFFFF"],
      strokeColors: "#6366F1",
    },
  };

  return (
    <div
      ref={chartRef}
      className="w-full max-w-[768px] my-8 p-2 rounded-2xl bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700  shadow-sm"
    >
      <h3 className="text-md p-4 font-QBold text-white mb-4">
        Daily Payment Summary
      </h3>
      <div className="relative h-[300px]">
        {showChart && (
          <ReactApexChart
            options={chartOptions}
            series={[{ name: "Sales Amount", data: salesData }]}
            type="area"
            height={300}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentChart;
